import { CONFIG } from '../../core/config';
import { EquipmentClass } from '../equipment/equipment-class';
import { EquipmentClassType } from '../equipment/equipment-class-type';
import { EquipmentComponent } from '../equipment/equipment-component';
import { EquipmentFunction } from '../equipment/equipment-function';
import { EquipmentPart } from '../equipment/equipment-part';
import { EquipmentSubUnit } from '../equipment/equipment-sub-unit';
import { Industry } from '../industry';
import { IndustryHierarchy } from '../industry-hierarchy/industry-hierarchy';
import { EquipmentClassSelection, IndustryHierarchySelection } from '../model';
import { ViewModel } from '../view-model';
import { ModelStatus } from '../model-status';
import { MaintenanceStrategyAttribute } from './maintenance-strategy-attribute';
import { MaintenanceStrategyBoundary } from './maintenance-strategy-boundary';
import { MaintenanceStrategyPlannedMaintenance } from './maintenance-strategy-planned-maintenance';

export class MaintenanceStrategy implements ViewModel, IndustryHierarchySelection, EquipmentClassSelection {
  readonly friendlyTypeNameSingular: string = 'Maintenance Strategy';
  readonly apiRoute: string = CONFIG.apiRoutes.maintenanceStrategyLibrary;

  id: string;

  equipmentLibraryId: string = CONFIG.constants.noneDropdownValue;

  industry: Industry = new Industry();
  industryHierarchyItem: IndustryHierarchy = new IndustryHierarchy();

  equipmentClassId: string = CONFIG.constants.noneDropdownValue;
  equipmentClassTypeId: string = CONFIG.constants.noneDropdownValue;
  equipmentSubUnitId: string = CONFIG.constants.noneDropdownValue;
  equipmentComponentId: string = CONFIG.constants.noneDropdownValue;
  equipmentPartId: string = CONFIG.constants.noneDropdownValue;

  equipmentClassItem: EquipmentClass = new EquipmentClass();
  equipmentClassType: EquipmentClassType = new EquipmentClassType();
  equipmentSubUnitItem: EquipmentSubUnit = new EquipmentSubUnit();
  equipmentComponentItem: EquipmentComponent = new EquipmentComponent();
  equipmentPartItem: EquipmentPart = new EquipmentPart();

  reference: string;
  title: string;
  boundary: MaintenanceStrategyBoundary = new MaintenanceStrategyBoundary();
  attributes: MaintenanceStrategyAttribute[] = [];
  plannedMaintenance: MaintenanceStrategyPlannedMaintenance[] = [];

  modelId: string;
  modelName: string;
  equipmentFunctions: EquipmentFunction[] = [];
  boundaryDiagramId: string;
  boundaryDiagramHeader: string;

  status: ModelStatus = ModelStatus.Draft;

  json() {
    return this;
  }

  loadFromJson(data: any) {
    this.id = data.id;

    this.equipmentLibraryId = data.equipmentLibraryId;

    this.industry = new Industry();
    if (data.industry) {
      this.industry.loadFromJson(data.industry);
    }

    this.industryHierarchyItem = new IndustryHierarchy();
    if (data.industryHierarchyItem) {
      this.industryHierarchyItem.loadFromJson(data.industryHierarchyItem);
    }

    this.equipmentClassId = data.equipmentClassId;
    this.equipmentClassTypeId = data.equipmentClassTypeId;
    this.equipmentSubUnitId = data.equipmentSubUnitId;
    this.equipmentComponentId = data.equipmentComponentId;
    this.equipmentPartId = data.equipmentPartId;

    if (data.equipmentClassItem) {
      this.equipmentClassItem = new EquipmentClass();
      this.equipmentClassItem.loadFromJson(data.equipmentClassItem);
    }

    if (data.equipmentClassType) {
      this.equipmentClassType = new EquipmentClassType();
      this.equipmentClassType.loadFromJson(data.equipmentClassType);
    }

    if (data.equipmentSubUnitItem) {
      this.equipmentSubUnitItem = new EquipmentSubUnit();
      this.equipmentSubUnitItem.loadFromJson(data.equipmentSubUnitItem);
    }

    if (data.equipmentComponentItem) {
      this.equipmentComponentItem = new EquipmentComponent();
      this.equipmentComponentItem.loadFromJson(data.equipmentComponentItem);
    }

    if (data.equipmentPartItem) {
      this.equipmentPartItem = new EquipmentPart();
      this.equipmentPartItem.loadFromJson(data.equipmentPartItem);
    }

    this.reference = data.reference;
    this.title = data.title;

    this.boundary = new MaintenanceStrategyBoundary();
    if (data.boundary) {
      this.boundary.loadFromJson(data.boundary);
    }

    this.boundaryDiagramId = data.boundaryDiagramId;
    this.boundaryDiagramHeader = data.boundaryDiagramHeader;

    this.attributes = [];
    if (data.attributes) {
      data.attributes.forEach(attribute => {
        const newAttribute = new MaintenanceStrategyAttribute();
        newAttribute.loadFromJson(attribute);
        this.attributes.push(newAttribute);
      });
    }

    this.plannedMaintenance = [];
    if (data.plannedMaintenance) {
      data.plannedMaintenance.forEach(maintenance => {
        const newPlannedMaintenance = new MaintenanceStrategyPlannedMaintenance();
        newPlannedMaintenance.loadFromJson(maintenance);
        this.plannedMaintenance.push(newPlannedMaintenance);
      });
    }

    this.modelId = data.modelId;
    this.modelName = data.modelName;
    this.status = data.status;

    this.equipmentFunctions = [];
    if (data.equipmentFunctions) {
      data.equipmentFunctions.forEach(equipmentFunction => {
        const newFunction = new EquipmentFunction();
        newFunction.loadFromJson(equipmentFunction);
        this.equipmentFunctions.push(newFunction);
      });
    }
  }
}
